import { useState, useEffect, useCallback } from "react";
import { faDownLong, faEdit } from "@fortawesome/free-solid-svg-icons";
import { withAppHOC } from "../../hoc";
import PageHeading from "../../components/PageHeader/PageHeader";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import NoData from "../../components/NoData/NoData";
import StatusHighliter from "../../components/StatusHighliter/StatusHighliter";
import {
  MessageBox,
  CustomButton,
  Label,
  Fieldset,
  TextInput,
  Button,
  Form,
  ResetButton,
  Dropdown,
  FormContainer,
  UpdateButton,
} from "../../components/FormElements";
import { TableBuilder } from "../../components/TableElements";
import Modal from "../../components/Modal";
import {
  StyledFiltersContainer,
  StyledLink,
  StyledModalContainer,
  StyledSpan,
} from "../../components/Styled";
import Pagination from "../../components/Pagination/Pagination";
import API from "../../api";
import { API_RESPONSE_TYPES, PERMISSION_TYPES } from "../../constants";
import {
  getSearchParams,
  shouldRedirect,
  redirectToPageOne,
} from "../../utils";
import FiltersButton from "../../components/FiltersButton/FiltersButton";
import moment from "moment";
import OverlayLoader from "../../components/OverlayLoader/OverlayLoader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const SellerAPISSupportTickets = (props) => {
  const [paginationData, setPaginationData] = useState({
    pageNumber: getSearchParams("pageNumber") || 1,
    recordsPerPage: getSearchParams("recordsPerPage") || 100,
  });
  const [totalRecords, setTotalRecords] = useState(0);
  const [isFiltering, setIsFiltering] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [responseStatus, setResponseStatus] = useState("");
  const [responseMessage, setResponseMessage] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [users, setUsers] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [usersId, setUsersId] = useState("");
  const [status, setStatus] = useState("");
  const [modalTransactionsId, setModalTransactionsId] = useState("");
  const [remark, setRemark] = useState("");
  const [showModal, setShowModal] = useState(false);

  const onClickShowFilters = () => {
    setShowFilters((showFilters) => !showFilters);
  };

  const resetResponseData = () => {
    setIsLoading(false);
    setResponseStatus("");
    setResponseMessage("");
  };

  const resetFilters = () => {
    setFromDate("");
    setToDate("");
    setUsersId("");
    setStatus("");
    readData();
  };

  const resetModalForm = () => {
    setModalTransactionsId("");
    setRemark("");
  };

  const readUsers = () => {
    API.get("/accounts-management/users").then((response) => {
      const { status, data } = response.data;
      if (status === API_RESPONSE_TYPES.SUCCESS) {
        setUsers(data);
      }
    });
  };

  const readData = useCallback(
    (fromDate = "", toDate = "", usersId = "", status = "") => {
      setIsLoading(true);
      setResponseStatus("");
      setResponseMessage("");
      API.get(
        `/help/seller-api-support?pageNumber=${paginationData.pageNumber}&recordsPerPage=${paginationData.recordsPerPage}&fromDate=${fromDate}&toDate=${toDate}&usersId=${usersId}&status=${status}`
      )
        .then((response) => {
          const { status, data, pageInfo } = response.data;
          if (shouldRedirect(data?.length, paginationData?.pageNumber)) {
            redirectToPageOne();
          } else {
            if (status === API_RESPONSE_TYPES.SUCCESS) {
              setTotalRecords(pageInfo.totalRecords);
            }
            setTickets(data);
          }
        })
        .catch((error) => {
          setResponseStatus(API_RESPONSE_TYPES.FAILED);
          setResponseMessage(error.message);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [paginationData]
  );

  const updateTicket = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setResponseStatus("");
    setResponseMessage("");
    API.put(`/help/seller-api-support/${modalTransactionsId}`, {
      remark,
      status: "Closed",
    })
      .then((response) => {
        const { status, message } = response.data;
        setResponseStatus(status);
        setResponseMessage(message);
        if (status === API_RESPONSE_TYPES.SUCCESS) {
          reloadData();
          setShowModal(false);
          setTimeout(() => {
            resetResponseData();
          }, 3000);
        }
      })
      .catch((error) => {
        setResponseStatus(API_RESPONSE_TYPES.FAILURE);
        setResponseMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const reloadData = () => {
    readData(fromDate, toDate, usersId, status);
  };

  const filterData = (e) => {
    e.preventDefault();
    setIsFiltering(true);
  };

  useEffect(() => {
    if (isFiltering) {
      setPaginationData((paginationData) => ({
        pageNumber: 1,
        recordsPerPage: paginationData.recordsPerPage,
      }));
      setIsFiltering((isFiltering) => !isFiltering);
    }
  }, [isFiltering]);

  useEffect(() => {
    readUsers();
  }, []);

  useEffect(() => {
    if (!isFiltering) {
      readData(fromDate, toDate, usersId, status);
    }
  }, [paginationData, readData]);

  const { accountsPermissions } = props;
  const canUpdate =
    accountsPermissions.includes(PERMISSION_TYPES.UPDATE) || false;

  const userOptions = [{ title: "-- SELECT USER --", value: "" }];
  if (users && users.length > 0) {
    users.forEach(({ id, fullName, mobileNumber }) =>
      userOptions.push({
        title: fullName,
        value: id,
        subTitle: mobileNumber,
      })
    );
  }

  return (
    <>
      <PageHeading {...props} reloadData={reloadData} showExport={false} />
      <FiltersButton onClick={onClickShowFilters} value={showFilters} />
      <StyledFiltersContainer className={showFilters ? "show" : ""}>
        <Form method="POST" action="#" onSubmit={filterData}>
          <Fieldset>
            <Label>User</Label>
            <Dropdown
              placeholder="User"
              value={usersId}
              onChange={setUsersId}
              options={userOptions}
              disabled={isLoading}
              searchable
            />
          </Fieldset>
          <Fieldset>
            <Label>Status</Label>
            <Dropdown
              value={status}
              onChange={setStatus}
              placeholder="Status"
              options={[
                { title: "-- SELECT STATUS --", value: "" },
                { title: "Open", value: "Open" },
                { title: "Closed", value: "Closed" },
              ]}
              disabled={isLoading}
            />
          </Fieldset>
          <Fieldset>
            <Label>From Date</Label>
            <TextInput
              type="date"
              value={fromDate}
              onChange={setFromDate}
              placeholder="From Date"
              disabled={isLoading}
            />
          </Fieldset>
          <Fieldset>
            <Label>To Date</Label>
            <TextInput
              type="date"
              value={toDate}
              onChange={setToDate}
              placeholder="To Date"
              disabled={isLoading}
            />
          </Fieldset>
          <Fieldset>
            <Button disabled={isLoading} type="submit">
              Filter
            </Button>
            <ResetButton
              disabled={isLoading}
              type="button"
              onClick={resetFilters}
            >
              Reset
            </ResetButton>
          </Fieldset>
        </Form>
      </StyledFiltersContainer>
      <LoadingSpinner
        isLoading={
          !showModal &&
          responseStatus === "" &&
          tickets?.length === 0 &&
          isLoading
        }
      />
      <NoData
        status={
          responseStatus !== API_RESPONSE_TYPES.FAILED &&
          !isLoading &&
          tickets?.length === 0
        }
        message={`No tickets found`}
      />
      {!showModal && (
        <MessageBox status={responseStatus} message={responseMessage} />
      )}
      {tickets?.length > 0 && (
        <>
          <TableBuilder
            isLoading={!showModal && tickets?.length !== 0 && isLoading}
            tableHeadings={[
              {
                title: "",
                dataSelector: "id",
                sticky: true,
                canSort: false,
                width: "100px",
                CellRenderer: (value, item) => (
                  <>
                    {canUpdate && item.status === "Open" && (
                      <CustomButton
                        icon={faEdit}
                        onClick={() => {
                          setModalTransactionsId(value);
                          setRemark("");
                          setShowModal(true);
                        }}
                        style={{ color: "#0095ff" }}
                      />
                    )}
                  </>
                ),
              },
              {
                title: "Date & Time",
                dataSelector: "createdOn",
                dataType: "string",
              },
              {
                title: "Ticket Id",
                dataSelector: "id",
                dataType: "string",
              },
              {
                title: "User",
                dataSelector: "fullName",
                dataType: "string",
                CellRenderer: (value, item) => (
                  <>
                    {value}
                    <StyledSpan>{item.mobileNumber}</StyledSpan>
                  </>
                ),
              },
              {
                title: "Website URL",
                dataSelector: "url",
                dataType: "string",
                CellRenderer: (value) => (value ? value : "N/A"),
              },
              {
                title: "Username",
                dataSelector: "username",
                dataType: "string",
                CellRenderer: (value) => (value ? value : "N/A"),
              },
              {
                title: "Password",
                dataSelector: "password",
                dataType: "string",
                CellRenderer: (value) => (value ? value : "N/A"),
              },
              {
                title: "Support Number",
                dataSelector: "supportNumber",
                dataType: "string",
                CellRenderer: (value) => (value ? value : "N/A"),
              },
              {
                title: "Server IP",
                dataSelector: "serverIp",
                dataType: "string",
                CellRenderer: (value) => (value ? value : "N/A"),
              },
              {
                title: "API Token",
                dataSelector: "apiToken",
                dataType: "string",
                CellRenderer: (value) => value || "N/A",
              },
              {
                title: "API Key",
                dataSelector: "apiKey",
                dataType: "string",
                CellRenderer: (value) => value || "N/A",
              },
              {
                title: "API Pin",
                dataSelector: "apiPin",
                dataType: "string",
                CellRenderer: (value) => value || "N/A",
              },
              {
                title: "API Remark",
                dataSelector: "apiRemark",
                dataType: "string",
                CellRenderer: (value) => value || "N/A",
              },
              {
                title: "API Document",
                dataSelector: "apiDocument",
                dataType: "string",
                CellRenderer: (value) =>
                  value ? (
                    <>
                      <StyledLink to={value} title="Download" target="_blank">
                        <FontAwesomeIcon icon={faDownLong} />
                      </StyledLink>
                    </>
                  ) : (
                    "N/A"
                  ),
              },
              {
                title: "Status",
                dataSelector: "status",
                dataType: "string",
                align: "center",
                CellRenderer: (value) => (
                  <StatusHighliter
                    className={
                      value === "Open"
                        ? "green"
                        : value === "Closed"
                        ? "red"
                        : "yellow"
                    }
                  >
                    {value}
                  </StatusHighliter>
                ),
              },
              {
                title: "Remark",
                dataSelector: "remark",
                dataType: "string",
                CellRenderer: (value) => (value ? value : "N/A"),
              },
              {
                title: "Closed On",
                dataSelector: "updatedOn",
                dataType: "string",
                CellRenderer: (value) => (value ? value : "N/A"),
              },
            ]}
            tableData={tickets}
          />
          <Pagination
            totalRecords={totalRecords}
            paginationData={paginationData}
            setPaginationData={setPaginationData}
          />
          <Modal
            modalStatus={showModal}
            setModalStatus={() => {
              resetResponseData();
              setShowModal(false);
              setModalTransactionsId("");
              setRemark("");
            }}
            hideCloseButton
          >
            <StyledModalContainer width={`1200px`}>
              <h3>Update Ticket</h3>
              <FormContainer>
                <Form method="POST" action="#" onSubmit={updateTicket}>
                  <Fieldset>
                    <Label required>Remark</Label>
                    <TextInput
                      value={remark}
                      onChange={setRemark}
                      placeholder="Please enter remark"
                      disabled={isLoading}
                    />
                  </Fieldset>
                  {showModal && (
                    <Fieldset>
                      <MessageBox
                        status={responseStatus}
                        message={responseMessage}
                      />
                    </Fieldset>
                  )}
                  <Fieldset>
                    <UpdateButton
                      disabled={isLoading === true}
                      type="submit"
                      style={{ width: "auto" }}
                    >
                      Update
                    </UpdateButton>
                    <ResetButton
                      disabled={isLoading === true}
                      type="button"
                      onClick={resetModalForm}
                    >
                      Reset
                    </ResetButton>
                  </Fieldset>
                  <OverlayLoader showLoader={showModal && isLoading} />
                </Form>
              </FormContainer>
            </StyledModalContainer>
          </Modal>
        </>
      )}
    </>
  );
};

export default withAppHOC(SellerAPISSupportTickets);
